<script setup>
// Quiz check-in body: single question, multiple choice, attempts + cooldown +
// lockout. Correct answer emits success after 500ms.
// With a `submit` handler (live pass) the picked answer id is sent to the
// backend and the SERVER decides (it also enforces attempts/cooldown);
// validation must never happen client-side. Local correctIndex compare
// remains only for the mock demo.
// Port of `QuizBody` in the source `_shared/CheckInSheet.tsx`.
import { ref, computed, watch, onUnmounted } from 'vue'
import { Lock } from '@lucide/vue'
import { computeCheckInAvailability } from './checkin'
import BlockedNotice from './BlockedNotice.vue'

const props = defineProps({
  spec: { type: Object, required: true },
  // Live validation: async ({ answer }) => { ok, message }.
  submit: { type: Function, default: null },
})

const emit = defineEmits(['success', 'help'])

const max = computed(() => props.spec.maxAttempts ?? 3)
const cooldown = computed(() => props.spec.cooldownSec ?? 0)
const picked = ref(null)
const submitted = ref(false)
const attempts = ref(0)
const cool = ref(0)
const locked = ref(false)
// Live verdict from the server: null | 'correct' | 'wrong'.
const serverVerdict = ref(null)
const availability = computed(() => computeCheckInAvailability(props.spec.details))
const blocked = computed(() => !availability.value.ok)

// Live choices are { id, text } objects (ids are what the server validates);
// mock choices are plain strings.
const choiceText = (c) => (typeof c === 'object' && c !== null ? c.text : c)
const choiceId = (c) => (typeof c === 'object' && c !== null ? c.id : c)

// Cooldown ticker
let coolTimer = null
watch(cool, (v) => {
  if (v > 0 && !coolTimer) {
    coolTimer = setInterval(() => {
      cool.value = Math.max(0, cool.value - 1)
    }, 1000)
  }
  if (v <= 0 && coolTimer) {
    clearInterval(coolTimer)
    coolTimer = null
  }
})

let successTimer = null
let retryTimer = null

// Wrong: bump attempts, apply cooldown or lockout, allow retry.
const failAttempt = () => {
  const nextAttempts = attempts.value + 1
  attempts.value = nextAttempts
  retryTimer = setTimeout(() => {
    if (nextAttempts >= max.value) {
      locked.value = true
    } else {
      cool.value = cooldown.value
      picked.value = null
      submitted.value = false
      serverVerdict.value = null
    }
  }, 900)
}

const handleSubmit = () => {
  if (locked.value || cool.value > 0 || picked.value === null || submitted.value) return
  submitted.value = true

  if (props.submit) {
    const answer = choiceId(props.spec.choices[picked.value])
    props.submit({ answer: [answer] })
      .then((result) => {
        if (result?.ok) {
          serverVerdict.value = 'correct'
          successTimer = setTimeout(() => emit('success'), 500)
          return
        }
        serverVerdict.value = 'wrong'
        failAttempt()
      })
      .catch(() => {
        serverVerdict.value = 'wrong'
        failAttempt()
      })
    return
  }

  if (picked.value === props.spec.correctIndex) {
    successTimer = setTimeout(() => emit('success'), 500)
    return
  }
  failAttempt()
}

onUnmounted(() => {
  clearInterval(coolTimer)
  clearTimeout(successTimer)
  clearTimeout(retryTimer)
})

// Live: the wrong/correct verdict comes from the server; mock: local compare.
const isCorrectPick = computed(() =>
  props.submit ? serverVerdict.value === 'correct' : submitted.value && picked.value === props.spec.correctIndex,
)
const wrongShown = computed(() => {
  if (props.submit) return serverVerdict.value === 'wrong'
  return submitted.value && picked.value !== null && picked.value !== props.spec.correctIndex
})
const attemptsLeft = computed(() => max.value - attempts.value)

const choiceState = (i) => {
  const isPick = picked.value === i
  if (isPick && isCorrectPick.value) return 'correct'
  if (isPick && wrongShown.value) return 'wrong'
  if (isPick) return 'picked'
  return 'idle'
}
</script>

<template>
  <!-- Locked view -->
  <div v-if="locked" class="cs-quiz-locked">
    <div class="cs-quiz-locked__badge">
      <Lock :size="24" />
    </div>
    <div class="cs-quiz-locked__title">Locked for {{ spec.lockoutMin ?? 60 }} min</div>
    <!-- No PIN exists on quiz specs — don't send people to the venue for one. -->
    <p class="cs-quiz-locked__hint">
      You used all {{ max }} attempts. Try again in {{ spec.lockoutMin ?? 60 }} min —
      you're still eligible today.
    </p>
    <div>
      <button
        id="checkin-quiz-locked-help"
        type="button"
        class="cs-quiz-locked__help gtm-click"
        @click="$emit('help')"
      >
        Still stuck? Chat with us
      </button>
    </div>
  </div>

  <div v-else class="cs-quiz">
    <BlockedNotice :availability="availability" />

    <div class="cs-quiz__meta">
      <span>Attempt {{ Math.min(attempts + 1, max) }} of {{ max }}</span>
      <span v-if="cool > 0" class="cs-quiz__meta-cool">Wait {{ cool }}s…</span>
    </div>

    <div class="cs-quiz__question" :class="{ 'cs-quiz--dimmed': blocked }">
      <p class="cs-quiz__question-text">{{ spec.question }}</p>
    </div>

    <div class="cs-quiz__choices" :class="{ 'cs-quiz--dimmed': blocked }">
      <button
        v-for="(c, i) in spec.choices"
        :id="`checkin-quiz-choice-${i}`"
        :key="i"
        type="button"
        class="cs-quiz__choice gtm-click"
        :class="`cs-quiz__choice--${choiceState(i)}`"
        :disabled="blocked || cool > 0 || submitted"
        @click="picked = i"
      >
        <span class="cs-quiz__choice-letter">{{ String.fromCharCode(65 + i) }}</span>
        {{ choiceText(c) }}
      </button>
    </div>

    <div v-if="wrongShown" class="cs-quiz__wrong">
      {{ attemptsLeft > 0
        ? `That's not right. ${attemptsLeft} attempt${attemptsLeft === 1 ? '' : 's'} left.`
        : "That's not right." }}
    </div>

    <div class="cs-quiz__footer">
      <button
        id="checkin-quiz-submit"
        type="button"
        class="cs-quiz__cta gtm-click"
        :disabled="blocked || picked === null || submitted || cool > 0"
        @click="handleSubmit"
      >
        <template v-if="isCorrectPick">Correct!</template>
        <template v-else-if="wrongShown">Try again</template>
        <template v-else-if="cool > 0">Wait {{ cool }}s…</template>
        <template v-else>Submit answer</template>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source locked view: `space-y-3 pt-3 text-center`
.cs-quiz-locked {
  padding-top: 12px; // pt-3
  text-align: center;

  > * + * {
    margin-top: 12px; // space-y-3
  }

  // Source: `mx-auto grid h-14 w-14 place-items-center rounded-full
  //          bg-rose-500/10 text-rose-500`
  &__badge {
    margin-left: auto;
    margin-right: auto;
    display: grid;
    height: 56px; // h-14
    width: 56px; // w-14
    place-items: center;
    border-radius: 9999px;
    background: rgba($cs-rose-500, 0.1);
    color: $cs-rose-500;
  }

  // Source: `font-serif-display text-lg font-black italic`
  &__title {
    font-family: var(--fr-font-serif-display, Georgia, 'Times New Roman', serif);
    font-size: 18px; // text-lg
    font-weight: 900; // font-black
    font-style: italic;
  }

  // Source: `text-[11px] ${theme.muted}`
  &__hint {
    margin: 0;
    font-size: 11px;
    color: var(--fr-muted, #6b7280);
  }

  &__help {
    @include cs-text-link;
  }
}

// Source: `space-y-3 pt-2`
.cs-quiz {
  padding-top: 8px; // pt-2

  > * + * {
    margin-top: 12px; // space-y-3
  }

  &--dimmed {
    opacity: 0.4;
  }

  // Source: `flex items-center justify-between text-[10px] font-bold
  //          uppercase tracking-wider ${theme.muted}`
  &__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--fr-muted, #6b7280);
  }

  &__meta-cool {
    color: $cs-rose-500;
  }

  // Source: `rounded-2xl bg-current/5 p-4 ring-1 ring-current/10`
  &__question {
    border-radius: 16px; // rounded-2xl
    background: cs-current(5%);
    padding: 16px; // p-4
    box-shadow: 0 0 0 1px cs-current(10%);
  }

  // Source: `font-serif-display text-base font-bold italic leading-snug`
  &__question-text {
    margin: 0;
    font-family: var(--fr-font-serif-display, Georgia, 'Times New Roman', serif);
    font-size: 16px; // text-base
    font-weight: 700;
    font-style: italic;
    line-height: 1.375; // leading-snug
  }

  // Source: `space-y-2`
  &__choices > * + * {
    margin-top: 8px; // space-y-2
  }

  // Source: `w-full rounded-xl px-3 py-3 text-left text-[13px] ring-1 transition`
  &__choice {
    @include cs-button-reset;
    display: block;
    width: 100%;
    border-radius: 12px; // rounded-xl
    padding: 12px; // px-3 py-3
    text-align: left;
    font-size: 13px;
    transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1),
      background-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
      box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1);

    // Source idle: `bg-current/5 ring-current/10 hover:ring-current/30`
    &--idle {
      background: cs-current(5%);
      box-shadow: 0 0 0 1px cs-current(10%);

      &:hover:not(:disabled) {
        box-shadow: 0 0 0 1px cs-current(30%);
      }
    }

    // Source picked: `${theme.primaryBtn} ring-transparent`
    &--picked {
      background: var(--fr-primary, #FF9900);
      color: var(--fr-primary-foreground, #232F3E);
      box-shadow: 0 0 0 1px transparent;
    }

    // Source correct: `bg-emerald-500/15 ring-emerald-500 text-emerald-700`
    &--correct {
      background: rgba($cs-emerald-500, 0.15);
      box-shadow: 0 0 0 1px $cs-emerald-500;
      color: $cs-emerald-700;
    }

    // Source wrong: `bg-rose-500/15 ring-rose-500 text-rose-700`
    &--wrong {
      background: rgba($cs-rose-500, 0.15);
      box-shadow: 0 0 0 1px $cs-rose-500;
      color: $cs-rose-700;
    }

    &:disabled {
      cursor: default;
    }
  }

  // Source: `mr-2 inline-grid h-5 w-5 place-items-center rounded-full
  //          bg-current/10 font-mono text-[10px] font-bold`
  &__choice-letter {
    margin-right: 8px; // mr-2
    display: inline-grid;
    height: 20px; // h-5
    width: 20px; // w-5
    place-items: center;
    border-radius: 9999px;
    background: cs-current(10%);
    font-family: $cs-font-mono;
    font-size: 10px;
    font-weight: 700;
    vertical-align: middle;
  }

  // Source: `text-center text-[11px] text-rose-500`
  &__wrong {
    text-align: center;
    font-size: 11px;
    color: $cs-rose-500;
  }

  &__footer {
    @include cs-sticky-footer;
  }

  &__cta {
    @include cs-cta;
  }
}
</style>
